import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Topbar from '../../mini-comp/Topbar';
import Sidebar from '../../mini-comp/Sidebar';
import axios from 'axios';
import Cookies from 'js-cookie';
import API_BASE_URL from '../../../../config/api';
import Swal from 'sweetalert2';
import {
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    Box,
    Chip
} from '@mui/material';

const EditNotice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [recipientType, setRecipientType] = useState('all');
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = Cookies.get('token');
                const [deptRes, empRes, noticeRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/v1/employees/department/`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }),
                    axios.get(`${API_BASE_URL}/api/v1/employees/`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }),
                    axios.get(`${API_BASE_URL}/api/v1/notices/${id}/`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    })
                ]);

                setDepartments(deptRes.data);
                setEmployees(Array.isArray(empRes.data) ? empRes.data : []);

                const notice = noticeRes.data;
                setTitle(notice.title);
                setContent(notice.content);
                setRecipientType(notice.recipient_type);

                if (notice.recipient_type === 'department') {
                    setSelectedDepartment(notice.department);
                } else if (notice.recipient_type === 'individual') {
                    setSelectedEmployees(notice.recipient_ids || []);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                Swal.fire('Error', 'Failed to load notice data.', 'error');
                navigate('/hr/notices');
            }
        };
        fetchData();
    }, [id, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const token = Cookies.get('token');

        const payload = {
            title,
            content,
            recipient_type: recipientType,
        };

        if (recipientType === 'department') {
            payload.department = selectedDepartment;
        } else if (recipientType === 'individual') {
            payload.recipient_ids = selectedEmployees;
        }

        try {
            await axios.patch(`${API_BASE_URL}/api/v1/notices/${id}/`, payload, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Notice updated successfully!',
                timer: 1500,
                showConfirmButton: false
            });

            navigate('/hr/notices');
        } catch (error) {
            console.error('Error updating notice:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update notice.'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEmployeeChange = (event) => {
        const { target: { value } } = event;
        setSelectedEmployees(typeof value === 'string' ? value.split(',') : value);
    };

    if (loading) {
        return (
            <div>
                <Topbar />
                <Sidebar />
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="page-title-box d-flex-between flex-wrap gap-15">
                                    <h1 className="page-title fs-18 lh-1">Edit Notice</h1>
                                </div>
                            </div>

                            <div className="col-xl-12">
                                <div className="card">
                                    <div className="card-body">
                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <label htmlFor="title" className="form-label">Title <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="title"
                                                    value={title}
                                                    onChange={(e) => setTitle(e.target.value)}
                                                    required
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="content" className="form-label">Content <span className="text-danger">*</span></label>
                                                <textarea
                                                    className="form-control"
                                                    id="content"
                                                    rows="5"
                                                    value={content}
                                                    onChange={(e) => setContent(e.target.value)}
                                                    required
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="recipientType" className="form-label">Recipient Type <span className="text-danger">*</span></label>
                                                <select
                                                    className="form-select"
                                                    id="recipientType"
                                                    value={recipientType}
                                                    onChange={(e) => setRecipientType(e.target.value)}
                                                >
                                                    <option value="all">All Employees</option>
                                                    <option value="department">Specific Department</option>
                                                    <option value="individual">Individual Employees</option>
                                                </select>
                                            </div>

                                            {recipientType === 'department' && (
                                                <div className="mb-3">
                                                    <label htmlFor="department" className="form-label">Select Department <span className="text-danger">*</span></label>
                                                    <select
                                                        className="form-select"
                                                        id="department"
                                                        value={selectedDepartment}
                                                        onChange={(e) => setSelectedDepartment(e.target.value)}
                                                        required
                                                    >
                                                        <option value="">Choose a department...</option>
                                                        {departments.map(dept => (
                                                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}

                                            {recipientType === 'individual' && (
                                                <div className="mb-3">
                                                    <label className="form-label">Select Employees <span className="text-danger">*</span></label>
                                                    <FormControl fullWidth>
                                                        <Select
                                                            multiple
                                                            value={selectedEmployees}
                                                            onChange={handleEmployeeChange}
                                                            input={<OutlinedInput />}
                                                            renderValue={(selected) => (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {selected.map((value) => {
                                                                        const emp = employees.find(e => e.id === value);
                                                                        return (
                                                                            <Chip key={value} label={emp ? `${emp.first_name} ${emp.last_name}` : value} />
                                                                        )
                                                                    })}
                                                                </Box>
                                                            )}
                                                            sx={{
                                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#86b7fe',
                                                                    borderWidth: '1px',
                                                                },
                                                            }}
                                                        >
                                                            {employees.map((emp) => (
                                                                <MenuItem key={emp.id} value={emp.id}>
                                                                    {emp.first_name} {emp.last_name} ({emp.email})
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </div>
                                            )}

                                            <div className="mt-4">
                                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                                    {submitting ? 'Updating...' : 'Update Notice'}
                                                </button>
                                                <button type="button" className="btn btn-secondary ms-2" onClick={() => navigate('/hr/notices')}>
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditNotice;
